import React from 'react'
import Layout from '../components/layout2'
import Container from '../components/container'
import Case from '../components/case'
import Vbox from '../components/vbox'
import Top from '../components/top'
import Summary from '../components/summary'
import Button from '../components/button'
import Img from '../components/img'
import ImageFlow from '../components/image-flow'

class RootIndex extends React.Component {
  render() {
    return (
      <Layout>
        <Container>
          <Vbox widegap>
            <Top
              title="SpeedEats"
              body="How do you make a food app feel as irresistible as crispy fries?"
              image="/speedeats1.png"
            />
            <Summary>
              <h4 className="h4">Course</h4>
              <p className="small">UI Design</p>
              <h4 className="h4">Challenge</h4>
              <p className="small">
                Design UI for a fast food delivery app with focus on "making it
                sexy" and creating a smooth and pleasant ordering flow.
              </p>
              <h4 className="h4">Use cases</h4>
              <p className="small">
                - The spontaneous cozy evening <br />
                - The fancy hangover breakfast <br />- The luxurious weekend
                brunch
              </p>
              <h4 className="h4">Target Audience?</h4>
              <p className="small">
                Someone who wants to cook as little as possible, who likes to
                treat themselves, and who often thinks "Let's get everything
                we're craving!"
              </p>
              <h4 className="h4">Timeframe</h4>
              <p className="small">About 2 weeks</p>
              <h4 className="h4">Result</h4>
              <p className="small">
                A fast, clear, and visually cohesive design that makes express
                orders as smooth as butter.
              </p>
            </Summary>
            <div>
              <h2 className="h2">1 - Background: </h2>
              <Container narrow>
                <h3 className="h3">The Story of SpeedEats</h3>
                <p className="body">
                  Tänk expressmat som en hybrid av Foodora Market och
                  HelloFresh.{' '}
                </p>

                <p className="body">
                  {' '}
                  The goal was to create a UI for an app where users could order
                  what they need in the moment and have it delivered in around
                  10 minutes. No big shopping trips, just immediate needs:
                  "we're out of milk", "need easy hangover food now!", "snack
                  emergency".
                </p>

                <p className="body">
                  Deliveries were enabled by bicycle couriers picking items from
                  small, centrally located warehouses with quick availability
                  and broad selection. To support this, the app needed to be
                  fast, clear, and easy to navigate. Plus, it needed to offer
                  pre-packaged product kits for different situations – similar
                  to HelloFresh, but in express format.
                </p>

                <p className="body">
                  Min uppgift? <br />
                  My task? <br />
                  To create the UI design from scratch: typography, colors,
                  spacing, components, and a visual style that makes the
                  experience as smooth as the delivery.
                </p>
              </Container>
            </div>

            <div>
              <h2 className="h2">2 - Results:</h2>

              <Container narrow>
                <p className="body">
                  <i>
                    "This is incredibly good design in general! Everything from
                    the indulgent branding through the logo, "the motif" (the
                    dripping fat and the wonderfully subtle fingerprint) and the
                    incredibly tight iconography, continuing with details like
                    "unfortunately sold out", discounted prices (in red),
                    complementary products that fit the specific moment, and so
                    on. Fantastic work!"
                  </i>{' '}
                  - Course Instructor
                </p>
                <h3 className="h3">SpeedEats</h3>
                <h4 className="h4">Smooth, Luxurious, and Indulgent</h4>
                <p className="body">
                  SpeedEats is a UI concept for a fast food delivery app
                  designed for quick, spontaneous needs rather than planned
                  shopping. With a focus on clear flows, strong visual
                  hierarchy, and a playful expression, the app makes it easy to
                  go from "hungry" to "ordered" in just a few clicks. The result
                  is an experience that embraces the indulgent side of fast food
                  - smooth to use, luxurious in feel, and just the right amount
                  of indulgent.
                </p>
                <Img narrow src="/portfolio/speedeatsfront.png" />
              </Container>
              <img src="/portfolio/resultatspeedeats.png" />
            </div>

            <div>
              <h2 className="h2">3 - Process:</h2>

              <Container narrow>
                <img
                  narrow
                  src="/portfolio/processflow.png"
                  style={{ margin: '64px 0' }}
                />
                <h3 className="h3">The Making of SpeedEats</h3>
                <h4 className="h4">From wireframes to cheesy express magic</h4>
                <p className="body">
                  The work started with existing wireframes, which meant that
                  focus early on was on expression, hierarchy, and visual unity.
                  From the start I had a clear idea that the app should feel
                  like a place for a little "indulgent" food: something quick,
                  delicious, and maybe not entirely well thought out.
                </p>
                <p className="body">
                  That feeling guided the branding. I wanted the expression to
                  be soft, warm, and almost a bit cheesy, which led to choosing
                  a yellow primary color combined with bulbous, round, and soft
                  typefaces.
                </p>
                <p className="body">
                  With the wireframes as a foundation, I also looked at similar
                  food apps for inspiration around structure, navigation, and
                  visual hierarchy. I started by designing one view at a time
                  and set up a clear component structure in Figma early on,
                  which made it easy to iterate, reuse patterns, and scale the
                  design to the remaining views.
                </p>
              </Container>

              <ImageFlow
                wide
                images={[
                  {
                    src: '/portfolio/pwire.png',
                    alt: 'Sketchy wireframes in PDF format',
                    title: '1. Start from existing wireframes',
                    body: 'Why reinvent the wheel? I had wireframes to work with, which allowed me to focus entirely on the visual language and UI components.',
                  },
                  {
                    src: '/portfolio/pblack.png',
                    alt: 'Own wireframes sketched in Figma',
                    title: '2. Draw and sketch layouts',
                    body: 'In Figma I sketched different layouts and component placements to quickly explore different visual hierarchies and flows.',
                  },
                  {
                    src: '/portfolio/pcomp.png',
                    alt: 'Example of structure in Figma with components, icons, and colors',
                    title: '3. Systematize and iterate',
                    body: 'Structure for colors, icons, and components in Figma that I could reuse and iterate on throughout the design process.',
                  },
                ]}
              />

              <Container narrow>
                <Img
                  narrow
                  src="/portfolio/colors.png"
                  caption="Colors, typography, and spacing scale that reflect the brand and that constrain design choices."
                />
              </Container>
            </div>
            <div>
              <h2 className="h2">4 - Summary:</h2>
              <Container narrow>
                <h3 className="h3">Insights & Learnings</h3>
                <h4 className="h4">Clear branding provides direction</h4>
                <p className="body">
                  Having a clear picture early on of what the app should be and
                  just as importantly, what it <i>should not</i> be - made
                  design decisions easier throughout the project. This was an
                  incredibly fun case where I felt free in my interpretation of
                  the task. The concept and keywords indulgent, luxurious, and
                  soft served as a throughline and gave the project both
                  momentum and a clear identity.
                </p>
                <h4 className="h4">Structure saves time and energy</h4>
                <p className="body">
                  Of course, it's important to first sketch freely to explore
                  ideas without getting stuck. But by working more
                  systematically based on UI principles and using tools like
                  components and reusable patterns, I got a lot "for free". A
                  clear structure made it easier to iterate, keep the whole
                  together, and scale the design without compromising quality.
                </p>
              </Container>
            </div>

            <h2 className="h2">More Projects:</h2>
            <div className="more-wrapper">
              <div class="more">
                <img src="/forum.png" />
                <h3 className="h3">Library Forum</h3>
                <Button
                  to="/forum"
                  ariaLabel="Explore the full project for Library Forum"
                >
                  Explore the full project
                </Button>
              </div>
              <div class="more">
                <img src="/vattenfall.png" />
                <h3 className="h3">Design System B2B Portals</h3>
                <Button
                  to="/vattenfall"
                  ariaLabel="Explore the full project for Design System B2B Portals"
                >
                  Explore the full project
                </Button>
              </div>
            </div>
          </Vbox>
        </Container>
      </Layout>
    )
  }
}

export default RootIndex
